import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Apple, ArrowLeft, ArrowUpRight, Clock, Play } from "lucide-react";
import { POSTS } from "@/data/posts";
import { PostCard } from "@/pages/Blog";
import { Reveal } from "@/components/landing/Reveal";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/constants/site";

const Block = ({ block }) => {
  if (block.type === "h2") {
    return (
      <h2 className="mt-12 text-2xl font-extrabold tracking-tight text-splurge-ink sm:text-3xl">
        {block.text}
      </h2>
    );
  }
  if (block.type === "ul") {
    return (
      <ul className="mt-6 space-y-3">
        {block.items.map((item) => (
          <li key={item} className="flex gap-3 text-base leading-relaxed text-splurge-ink/70">
            <span
              aria-hidden="true"
              className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-splurge-primary"
            />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return <p className="mt-6 text-base leading-relaxed text-splurge-ink/70 md:text-lg">{block.text}</p>;
};

export const BlogPost = () => {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Splurge Finance Blog`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", post.excerpt);
  }, [post]);

  if (!post) {
    return (
      <main id="main" className="bg-splurge-bg">
        <div className="mx-auto max-w-3xl px-5 py-32 text-center sm:px-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-splurge-ink">
            Article not found
          </h1>
          <p className="mt-4 text-splurge-ink/60">
            This post may have moved. Browse all our money tips instead.
          </p>
          <Link
            to="/blog"
            data-testid="blog-not-found-back"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-splurge-primary px-6 py-3 text-sm font-bold text-white"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All articles
          </Link>
        </div>
      </main>
    );
  }

  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 2);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.dateISO,
    author: { "@type": "Organization", name: "Splurge Finance" },
    publisher: { "@type": "Organization", name: "Splurge Finance" },
    keywords:
      "student finance app, expense tracker for students, shared expenses, financial literacy, mindful spending, India",
  };

  return (
    <main id="main" className="bg-splurge-bg">
      <article className="mx-auto max-w-3xl px-5 py-16 sm:px-8 lg:py-20">
        <Reveal>
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm font-bold text-splurge-primary transition-colors hover:text-splurge-dark"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All articles
          </Link>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs font-bold">
            <span className="rounded-full bg-splurge-lavender px-3 py-1 text-splurge-dark">
              {post.category}
            </span>
            <time dateTime={post.dateISO} className="text-splurge-ink/45">
              {post.dateLabel}
            </time>
            <span className="flex items-center gap-1.5 text-splurge-ink/45">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {post.readMinutes} min read
            </span>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <h1 className="mt-6 text-3xl font-extrabold leading-[1.12] tracking-tight text-splurge-ink sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-8 border-t border-splurge-lavender pt-2">
            {post.blocks.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-14 rounded-3xl bg-gradient-to-br from-splurge-dark via-splurge-primary to-splurge-dark p-8 text-center sm:p-10">
            <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              Put this into practice with Splurge.
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-white/70">
              Track spending, settle shared expenses, and see real-life trade-offs, free on iOS
              and Android.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="blog-post-cta"
                aria-label="Download Splurge on the App Store (opens in a new tab)"
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-extrabold text-splurge-dark shadow-xl transition-all duration-200 hover:-translate-y-0.5 hover:bg-splurge-lavender active:scale-95"
              >
                <Apple className="h-4 w-4" aria-hidden="true" />
                App Store
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="blog-post-cta-android"
                aria-label="Get Splurge on Google Play (opens in a new tab)"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-7 py-3.5 text-sm font-extrabold text-white transition-all duration-200 hover:-translate-y-0.5 hover:border-white hover:bg-white/10 active:scale-95"
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                Google Play
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </Reveal>
      </article>

      <section
        aria-label="Related articles"
        className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32"
      >
        <h2 className="text-2xl font-extrabold tracking-tight text-splurge-ink sm:text-3xl">
          Keep reading
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {related.map((p) => (
            <PostCard key={p.slug} post={p} />
          ))}
        </div>
      </section>

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </main>
  );
};
